// src/components/HRComponents/EmployeePolicyView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';

import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import AckConfirmationDialog from './AckConfirmationDialog';
import PolicyDocumentViewer from './PolicyDocumentViewer';
import ContentProtector from '../../helpers/ContentProtector';

const EmployeePolicyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [showAckDialog, setShowAckDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [myAck, setMyAck] = useState(null);

  // Fetch document details and existing user acknowledgement logs
  const fetchDoc = useCallback(async () => {
    setLoading(true);
    try {
      const [docRes, myRes] = await Promise.all([
        api.get(SummaryApi.getPolicyById.url(id)),
        api.get(SummaryApi.getMyPolicies.url),
      ]);
      if (docRes.data.success) setDoc(docRes.data.document);
      if (myRes.data.success) {
        const all = [...(myRes.data.pending || []), ...(myRes.data.acknowledged || [])];
        setMyAck(all.find((a) => String(a.policyDocumentId?._id) === String(id)));
      }
    } catch {
      toast.error('Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  // Submit digital acceptance on confirmation
  const handleAcknowledge = async () => {
    setIsSubmitting(true);
    try {
      await api.post(SummaryApi.acknowledgePolicy.url(id), { digitalAcceptance: true });
      toast.success('Acknowledged successfully');
      setShowAckDialog(false);
      fetchDoc();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to acknowledge');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle downloading of the published document version
  // const handleDownload = async () => {
  //   try {
  //     const res = await api.get(SummaryApi.downloadPolicy.url(id), { responseType: 'blob' });
  //     const url = window.URL.createObjectURL(new Blob([res.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', doc.currentVersionId?.fileName || `${doc.title}.pdf`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch {
  //     toast.error('Download failed');
  //   }
  // };

  if (loading || !doc) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  const isAcknowledged = myAck?.status === 'acknowledged';

  return (
    <div className="p-4">
      <ContentProtector />
      {/* Header panel with title and download button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="rounded-lg bg-red-50 border border-red-100 p-1.5 sm:p-2">
            <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">{doc.title}</h1>
        </div>
        {/* <button onClick={handleDownload} className="flex items-center gap-2 bg-white border px-3 py-2 rounded-lg text-sm">
          <Download className="h-4 w-4" /> Download
        </button> */}
      </div>

      {/* Document preview viewport */}
      {doc.currentVersionId && (
        <PolicyDocumentViewer
          policyId={id}
          fileUrl={doc.currentVersionId.fileUrl}
          mimeType={doc.currentVersionId.fileMimeType}
          fileName={doc.currentVersionId.fileName}
        />
      )}

      {/* Action panel displaying acknowledgement status or action button */}
      <div className="mt-6 flex justify-center">
        {isAcknowledged ? (
          <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
            <ShieldCheck className="h-5 w-5" /> You acknowledged this document
          </span>
        ) : (
          <button
            onClick={() => setShowAckDialog(true)}
            className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg text-sm font-medium"
          >
            <ShieldCheck className="h-4 w-4" /> I Have Read & I Accept
          </button>
        )}
      </div>

      {/* FIX: Conditional rendering to cleanly reset Modal checkbox state on close [1] */}
      {showAckDialog && (
        <AckConfirmationDialog
          open={showAckDialog}
          policyTitle={doc.title}
          onConfirm={handleAcknowledge}
          onClose={() => setShowAckDialog(false)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default EmployeePolicyView;